/**
 * Offscreen canvases for cached drawings (glows, sprites, backdrop strips) and the cache
 * that keeps them. Everything expensive to draw is drawn once into a canvas and then
 * stamped with drawImage — no shadowBlur, no per-frame gradients.
 */
import type { Ctx2D } from './icons';

/** A canvas with its 2D context; null outside a browser (tests) or when the context fails. */
export interface Layer {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
}

export function createLayer(width: number, height: number): Layer | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.ceil(width));
  canvas.height = Math.max(1, Math.ceil(height));
  const ctx = canvas.getContext('2d');
  return ctx ? { canvas, ctx } : null;
}

/** A small LRU cache of drawn canvases. */
export class SpriteCache {
  private readonly entries = new Map<string, HTMLCanvasElement | null>();

  constructor(private readonly limit = 64) {}

  get size(): number {
    return this.entries.size;
  }

  /** The cached canvas for `key`, drawn by `create` the first time (null = cannot draw). */
  get(key: string, create: () => HTMLCanvasElement | null): HTMLCanvasElement | null {
    const hit = this.entries.get(key);
    if (hit !== undefined) {
      // Refresh its place in the LRU order.
      this.entries.delete(key);
      this.entries.set(key, hit);
      return hit;
    }
    const made = create();
    this.entries.set(key, made);
    if (this.entries.size > this.limit) {
      const oldest = this.entries.keys().next().value;
      if (oldest !== undefined) this.entries.delete(oldest);
    }
    return made;
  }

  clear(): void {
    this.entries.clear();
  }
}

/**
 * Big shared canvases (the backdrop layers, up to the size of a view) with reference counts:
 * users of the same key share one canvas, and a canvas nobody uses any more waits as a spare
 * (a view switching back reuses it). Beyond `spares` unused ones the oldest is dropped and
 * its pixels released at once (width = height = 0): iOS Safari caps the memory of all
 * canvases together and frees a dropped canvas only when the garbage collector gets to it,
 * while sizes change with every rotation and resolution step.
 */
export class LayerPool {
  private readonly layers = new Map<string, { canvas: HTMLCanvasElement | null; users: number }>();
  /** Keys of the layers nobody uses, oldest first. */
  private readonly unused: string[] = [];

  constructor(private readonly spares = 4) {}

  /** Layers alive: in use or spare. */
  get size(): number {
    return this.layers.size;
  }

  /** Layers somebody uses. */
  get inUse(): number {
    return this.layers.size - this.unused.length;
  }

  /** The canvas for `key`, drawn by `create` if there is none; give it back with release(). */
  acquire(key: string, create: () => HTMLCanvasElement | null): HTMLCanvasElement | null {
    let layer = this.layers.get(key);
    if (!layer) {
      layer = { canvas: create(), users: 0 };
      this.layers.set(key, layer);
    } else if (layer.users === 0) {
      this.unused.splice(this.unused.indexOf(key), 1);
    }
    layer.users++;
    return layer.canvas;
  }

  release(key: string): void {
    const layer = this.layers.get(key);
    if (!layer || layer.users === 0 || --layer.users > 0) return;
    this.unused.push(key);
    while (this.unused.length > this.spares) {
      const oldest = this.unused.shift()!;
      const canvas = this.layers.get(oldest)?.canvas;
      this.layers.delete(oldest);
      if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
      }
    }
  }
}

/**
 * Draws `image` centred at (x, y) with size w × h, rotated by `rotation` (radians) and
 * mirrored vertically when `flip` is set. Coordinates are CSS pixels on a context whose base
 * transform is a plain `scale` (the device pixel ratio), which is restored afterwards.
 */
export function stamp(
  ctx: Ctx2D,
  scale: number,
  image: CanvasImageSource,
  x: number,
  y: number,
  w: number,
  h: number,
  rotation = 0,
  flip = false,
): void {
  if (rotation === 0 && !flip) {
    ctx.drawImage(image, x - w / 2, y - h / 2, w, h);
    return;
  }
  const cos = Math.cos(rotation) * scale;
  const sin = Math.sin(rotation) * scale;
  const f = flip ? -1 : 1;
  ctx.setTransform(cos, sin, -sin * f, cos * f, x * scale, y * scale);
  ctx.drawImage(image, -w / 2, -h / 2, w, h);
  ctx.setTransform(scale, 0, 0, scale, 0, 0);
}
